import axios from 'axios';
import {
  GET_APPLICATION_DOCUMENTS_API_URL,
  GET_APPLICATION_FORM_API_URL,
  GET_APPLICATION_TRANSITION_HISTORY_API_URL,
  GET_PROFILE_COMPLETION_API_URL,
  LIST_SENT_CONTRACTS_URL,
    MANUAL_VERIFY_ADDITIONAL_DOCUMENT_URL,
    MANUAL_VERIFY_SECTION_QUESTION_API_URL,
    GET_MANUAL_VERIFICATION_HISTORY,
    GET_MANUAL_VERIFICATION_QUESTION_MAPPINGS_API,
    GET_MANUAL_VERIFICATION_RUN,
    GET_MANUAL_VERIFICATION_STATUS,
    GET_BANK_STATEMENT_ANALYSIS_API,
    GET_ONBOARDING_VERIFICATION_QUESTION_MAPPINGS_API,
    GET_VERIFICATION_LOGS,
    GET_VERIFICATION_STATUSES,
    LIST_APPLICATION_FORMS_API_URL,
    LIST_ENTITY_PROFILES_API_URL,
    LIST_MANUAL_VERIFICATION_RUNS,
    OPEN_MANUAL_VERIFICATION_RUN,
    REFRESH_BANK_STATEMENT_ANALYSIS_API,
    RECORD_MANUAL_VERIFICATION_DECISION,
    RECORD_MANUAL_VERIFICATION_FIELD_RESULT,
    START_BANK_STATEMENT_ANALYSIS_API,
    SUBMIT_APPLICATION_FORM_ANSWERS_API_URL,
    get_auth_headers,
    get_request_config
  } from '@/utils/system_constants/constant.js';
import {
  extract_items,
  first_value,
  parse_response_data
} from '@/services/onboarding_data_helpers';
import {
  map_with_concurrency_limit,
  onboarding_request_cache
} from '@/services/onboarding_journey_service_helpers';

function clear_application_runtime_cache(application_id, application_form_id = null) {
  if (application_id) {
    onboarding_request_cache.application_forms.delete(String(application_id));
    onboarding_request_cache.application_documents.delete(String(application_id));
    onboarding_request_cache.application_transition_history.delete(String(application_id));
    onboarding_request_cache.application_profile_completion_summary.delete(String(application_id));
  }
  if (application_form_id) {
    onboarding_request_cache.application_form.delete(String(application_form_id));
    onboarding_request_cache.application_profile_completion.delete(String(application_form_id));
  }
  onboarding_request_cache.all_cases.clear();
  onboarding_request_cache.onboarding_status.clear();
  onboarding_request_cache.entity_profiles.clear();
}

export function clear_manual_verification_cache(entity_profile_id = null) {
  if (entity_profile_id) {
    const cache_key = String(entity_profile_id);
    onboarding_request_cache.manual_verification_history.delete(cache_key);
    onboarding_request_cache.manual_verification_status.delete(cache_key);
    return;
  }

  onboarding_request_cache.manual_verification_history.clear();
  onboarding_request_cache.manual_verification_status.clear();
}

export const fetch_entity_profile = async (entity_profile_id, params = {}) => {
  if (!entity_profile_id) return null;
  const cache_key = JSON.stringify({ entity_profile_id, ...params });
  if (onboarding_request_cache.entity_profiles.has(cache_key)) {
    return onboarding_request_cache.entity_profiles.get(cache_key);
  }

  const request = axios.get(
    LIST_ENTITY_PROFILES_API_URL,
    get_request_config({ params: { ...params, entity_profile_id } })
  )
    .then((response) => parse_response_data(response)?.data || null)
    .catch((error) => {
      onboarding_request_cache.entity_profiles.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.entity_profiles.set(cache_key, request);
  return request;
};

export const fetch_application_forms = async (application_id, options = {}) => {
  if (!application_id) return [];
  const cache_key = String(application_id);
  const force_refresh = options?.force_refresh === true;
  if (force_refresh) {
    onboarding_request_cache.application_forms.delete(cache_key);
  }
  if (onboarding_request_cache.application_forms.has(cache_key)) {
    return onboarding_request_cache.application_forms.get(cache_key);
  }

  const request = axios.get(
    LIST_APPLICATION_FORMS_API_URL,
    get_request_config({ params: { application_id } })
  )
    .then((response) => extract_items(response))
    .catch((error) => {
      onboarding_request_cache.application_forms.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.application_forms.set(cache_key, request);
  return request;
};

export const fetch_application_form = async (application_form_id, options = {}) => {
  if (!application_form_id) return null;
  const cache_key = String(application_form_id);
  const force_refresh = options?.force_refresh === true;
  if (force_refresh) {
    onboarding_request_cache.application_form.delete(cache_key);
  }
  if (onboarding_request_cache.application_form.has(cache_key)) {
    return onboarding_request_cache.application_form.get(cache_key);
  }

  const request = axios.get(
    GET_APPLICATION_FORM_API_URL,
    get_request_config({ params: { application_form_id } })
  )
    .then((response) => {
      const parsed = parse_response_data(response);

      if (parsed?.data && typeof parsed.data === 'object' && !Array.isArray(parsed.data)) {
        return parsed.data;
      }

      return first_value([
        parsed?.data?.application_form,
        parsed?.application_form,
        parsed?.data,
        parsed
      ], null);
    })
    .catch((error) => {
      onboarding_request_cache.application_form.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.application_form.set(cache_key, request);
  return request;
};

export const fetch_application_profile_completion = async (application_form_id) => {
  if (!application_form_id) return null;
  const cache_key = String(application_form_id);
  if (onboarding_request_cache.application_profile_completion.has(cache_key)) {
    return onboarding_request_cache.application_profile_completion.get(cache_key);
  }

  const request = axios.get(
    GET_PROFILE_COMPLETION_API_URL,
    get_request_config({ params: { application_form_id } })
  )
    .then((response) => parse_response_data(response)?.data || null)
    .catch(() => {
      onboarding_request_cache.application_profile_completion.delete(cache_key);
      return null;
    });

  onboarding_request_cache.application_profile_completion.set(cache_key, request);
  return request;
};

function get_application_form_id(form = {}) {
  return Number(
    form?.application_form_id
    || form?.id
    || form?.application_form?.id
    || 0
  );
}

function get_application_form_ids(forms = []) {
  return [...new Set(
    (Array.isArray(forms) ? forms : [])
      .map((form) => get_application_form_id(form))
      .filter((form_id) => form_id > 0)
  )];
}

async function fetch_profile_completion_results(application_form_ids = []) {
  return map_with_concurrency_limit(
    application_form_ids,
    (application_form_id) => (
      fetch_application_profile_completion(application_form_id).catch(() => null)
    ),
    4
  );
}

function is_valid_profile_completion(completion = null) {
  return Boolean(
    completion
    && Number.isFinite(Number(completion?.total_questions))
    && Number(completion.total_questions) >= 0
  );
}

function get_valid_profile_completions(completion_results = []) {
  return completion_results.filter((completion) => is_valid_profile_completion(completion));
}

function build_profile_completion_summary(valid_completions = []) {
  if (!valid_completions.length) return null;

  const total_questions = valid_completions.reduce((sum, completion) => (
    sum + Number(completion?.total_questions || 0)
  ), 0);
  const answered_questions = valid_completions.reduce((sum, completion) => (
    sum + Number(completion?.answered_questions || 0)
  ), 0);
  const unanswered_questions = Math.max(0, total_questions - answered_questions);
  const completion_percentage = total_questions > 0
    ? Number(((answered_questions / total_questions) * 100).toFixed(2))
    : 0;

  return {
    total_questions,
    answered_questions,
    unanswered_questions,
    completion_percentage,
    sections: valid_completions.flatMap((completion) => (
      Array.isArray(completion?.sections) ? completion.sections : []
    ))
  };
}

async function resolve_application_profile_completion_summary(application_id) {
  const forms = await fetch_application_forms(application_id);
  const application_form_ids = get_application_form_ids(forms);

  if (!application_form_ids.length) return null;

  const completion_results = await fetch_profile_completion_results(application_form_ids);
  const valid_completions = get_valid_profile_completions(completion_results);
  return build_profile_completion_summary(valid_completions);
}

export const fetch_application_profile_completion_summary = async (application_id) => {
  if (!application_id) return null;
  const cache_key = String(application_id);
  if (onboarding_request_cache.application_profile_completion_summary.has(cache_key)) {
    return onboarding_request_cache.application_profile_completion_summary.get(cache_key);
  }

  const request = resolve_application_profile_completion_summary(application_id)
    .catch(() => {
      onboarding_request_cache.application_profile_completion_summary.delete(cache_key);
      return null;
    });

  onboarding_request_cache.application_profile_completion_summary.set(cache_key, request);
  return request;
};

export const fetch_application_documents = async (application_id) => {
  if (!application_id) return [];
  const cache_key = String(application_id);
  if (onboarding_request_cache.application_documents.has(cache_key)) {
    return onboarding_request_cache.application_documents.get(cache_key);
  }

  const request = axios.get(
    GET_APPLICATION_DOCUMENTS_API_URL,
    get_request_config({ params: { application_id } })
  )
    .then((response) => extract_items(response))
    .catch((error) => {
      onboarding_request_cache.application_documents.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.application_documents.set(cache_key, request);
  return request;
};

export const verify_application_document = async ({
  application_id,
  application_form_id,
  section_id,
  question_id,
  is_verified,
  verification_comment = ''
} = {}) => {
  const response = await axios.post(
    MANUAL_VERIFY_SECTION_QUESTION_API_URL,
    {
      application_form_id,
      section_id,
      question_id,
      is_verified,
      verification_comment
    },
    get_request_config()
  );

  clear_application_runtime_cache(application_id, application_form_id);
  return parse_response_data(response);
};

export const verify_additional_application_document = async ({
  application_id,
  additional_document_id,
  is_verified,
  verification_comment = ''
} = {}) => {
  const response = await axios.post(
    MANUAL_VERIFY_ADDITIONAL_DOCUMENT_URL,
    {
      additional_document_id,
      is_verified,
      verification_comment
    },
    get_request_config()
  );

  clear_application_runtime_cache(application_id);
  return parse_response_data(response);
};

export const fetch_application_transition_history = async (application_id) => {
  if (!application_id) return [];
  const cache_key = String(application_id);
  if (onboarding_request_cache.application_transition_history.has(cache_key)) {
    return onboarding_request_cache.application_transition_history.get(cache_key);
  }

  const request = axios.get(
    GET_APPLICATION_TRANSITION_HISTORY_API_URL,
    get_request_config({ params: { application_id } })
  )
    .then((response) => parse_response_data(response)?.data || [])
    .catch((error) => {
      onboarding_request_cache.application_transition_history.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.application_transition_history.set(cache_key, request);
  return request;
};

export const fetch_sent_contracts = async (entity_profile_id) => {
  if (!entity_profile_id) return [];

  const response = await axios.get(
    LIST_SENT_CONTRACTS_URL,
    get_request_config({ params: { entity_profile_id } })
  );

  const payload = response?.data || {};
  return Array.isArray(payload?.data) ? payload.data : [];
};

export const fetch_verification_statuses = async (application_form_id) => {
  if (!application_form_id) return {};

  const response = await axios.get(
    GET_VERIFICATION_STATUSES,
    get_request_config({ params: { application_form_id } })
  );
  return parse_response_data(response)?.data || {};
};

export const fetch_verification_logs = async (entity_profile_id) => {
  if (!entity_profile_id) return [];

  const response = await axios.get(
    GET_VERIFICATION_LOGS,
    get_request_config({ params: { entity_profile_id } })
  );
  const parsed = parse_response_data(response);
  return Array.isArray(parsed?.logs) ? parsed.logs : [];
};

export const fetch_manual_verification_history = async (entity_profile_id) => {
  if (!entity_profile_id) return [];
  const cache_key = String(entity_profile_id);
  if (onboarding_request_cache.manual_verification_history.has(cache_key)) {
    return onboarding_request_cache.manual_verification_history.get(cache_key);
  }

  const request = axios.get(
    GET_MANUAL_VERIFICATION_HISTORY,
    get_request_config({ params: { entity_profile_id } })
  )
    .then((response) => parse_response_data(response)?.data || [])
    .catch((error) => {
      onboarding_request_cache.manual_verification_history.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.manual_verification_history.set(cache_key, request);
  return request;
};

export const fetch_manual_verification_status = async (entity_profile_id) => {
  if (!entity_profile_id) return {};
  const cache_key = String(entity_profile_id);
  if (onboarding_request_cache.manual_verification_status.has(cache_key)) {
    return onboarding_request_cache.manual_verification_status.get(cache_key);
  }

  const request = axios.get(
    GET_MANUAL_VERIFICATION_STATUS,
    get_request_config({ params: { entity_profile_id } })
  )
    .then((response) => parse_response_data(response)?.data || {})
    .catch((error) => {
      onboarding_request_cache.manual_verification_status.delete(cache_key);
      throw error;
    });

  onboarding_request_cache.manual_verification_status.set(cache_key, request);
  return request;
};

export const open_manual_verification_run = async (payload) => {
  const response = await axios.post(
    OPEN_MANUAL_VERIFICATION_RUN,
    payload,
    get_request_config()
  );
  clear_manual_verification_cache(payload?.entity_profile_id);
  return parse_response_data(response);
};

export const fetch_manual_verification_run = async (run_id) => {
  if (!run_id) return null;

  const response = await axios.get(
    GET_MANUAL_VERIFICATION_RUN,
    get_request_config({ params: { run_id } })
  );
  return parse_response_data(response)?.data || null;
};

export const fetch_manual_verification_runs = async (params = {}) => {
  const response = await axios.get(
    LIST_MANUAL_VERIFICATION_RUNS,
    get_request_config({ params })
  );
  return parse_response_data(response)?.data || [];
};

export const fetch_onboarding_verification_question_mappings = async (form_link_id) => {
  if (!form_link_id) return [];

  const response = await axios.get(
    GET_ONBOARDING_VERIFICATION_QUESTION_MAPPINGS_API,
    get_request_config({ params: { form_link_id, only_active: true } })
  );
  return parse_response_data(response)?.data || [];
};

export const fetch_manual_verification_question_mappings = async (form_link_id) => {
  if (!form_link_id) return [];

  const response = await axios.get(
    GET_MANUAL_VERIFICATION_QUESTION_MAPPINGS_API,
    get_request_config({ params: { form_link_id, only_active: true } })
  );
  return parse_response_data(response)?.data || [];
};

export const record_manual_verification_decision = async (payload) => {
  const response = await axios.post(
    RECORD_MANUAL_VERIFICATION_DECISION,
    payload,
    get_request_config()
  );
  clear_manual_verification_cache(payload?.entity_profile_id);
  return parse_response_data(response);
};

export const record_manual_verification_field_result = async (payload) => {
  const response = await axios.post(
    RECORD_MANUAL_VERIFICATION_FIELD_RESULT,
    payload,
    get_request_config()
  );
  clear_manual_verification_cache(payload?.entity_profile_id);
  return parse_response_data(response);
};

export const start_bank_statement_analysis = async (payload) => {
  const response = await axios.post(
    START_BANK_STATEMENT_ANALYSIS_API,
    payload,
    get_request_config()
  );
  clear_manual_verification_cache(payload?.entity_profile_id);
  return parse_response_data(response)?.data || null;
};

export const refresh_bank_statement_analysis = async (payload) => {
  const response = await axios.post(
    REFRESH_BANK_STATEMENT_ANALYSIS_API,
    payload,
    get_request_config()
  );
  clear_manual_verification_cache(payload?.entity_profile_id);
  return parse_response_data(response)?.data || null;
};

export const fetch_bank_statement_analysis = async (run_id) => {
  if (!run_id) return null;

  const response = await axios.get(
    GET_BANK_STATEMENT_ANALYSIS_API,
    get_request_config({ params: { run_id } })
  );
  return parse_response_data(response)?.data || null;
};

export const submit_application_form_answers = async (payload) => {
  const response = await axios.post(SUBMIT_APPLICATION_FORM_ANSWERS_API_URL, payload, get_request_config());
  clear_application_runtime_cache(payload?.application_id, payload?.application_form_id);
  return parse_response_data(response);
};

export const submit_application_form_file_answers = async ({
  application_id = null,
  application_form_id,
  answers = [],
  files = {},
  repeatable_groups = []
}) => {
  const form_data = new FormData();
  form_data.append('application_form_id', String(application_form_id));
  if (application_id) form_data.append('application_id', String(application_id));
  form_data.append('answers', JSON.stringify(answers));
  form_data.append('repeatable_groups', JSON.stringify(repeatable_groups));

  Object.entries(files).forEach(([field_name, file]) => {
    const file_list = Array.isArray(file) ? file : [file];
    file_list.forEach((file_item) => {
      if (file_item) form_data.append(field_name, file_item);
    });
  });

  const response = await axios.post(SUBMIT_APPLICATION_FORM_ANSWERS_API_URL, form_data, {
    withCredentials: true,
    headers: {
      ...get_auth_headers()
    }
  });

  clear_application_runtime_cache(application_id, application_form_id);
  return parse_response_data(response);
};
